import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { isPreviewMode } from './preview.util';
import { previewFixture } from './preview.fixtures';

/**
 * When PREVIEW MODE is on, answer app-API requests with fixtures instead of
 * hitting the backend. Passes everything else through untouched. When preview
 * is off this interceptor does nothing at all.
 */
@Injectable()
export class PreviewInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!isPreviewMode()) return next.handle(req);

    // never touch auth0, static assets, maps tiles, geoserver, baked terrain, map markers, town plans, map outline
    if (
      /auth0\.com|\.svg|\.png|\.jpe?g|\.woff2?|\/assets\/|geoserver|tile|openstreetmap|api\/terrain\/|api\/markers\/|api\/map-outline\/|getcitydata\//i.test(
        req.url,
      )
    ) {
      return next.handle(req);
    }

    // urlWithParams, not url: Angular keeps query params off `req.url`, so a
    // fixture could never distinguish /reviews/?vendor=x from /reviews/?listing=y.
    // Safe for the existing `$`-anchored patterns — those all match calls that
    // send no params.
    const body = previewFixture(req.urlWithParams, req.method);
    if (body !== undefined) {
      return of(new HttpResponse({ status: 200, body })).pipe(delay(120));
    }

    // any other mutating call → pretend it worked, so UI flows don't error out
    if (req.method !== 'GET') {
      return of(new HttpResponse({ status: 200, body: { success: true, preview: true } })).pipe(
        delay(120),
      );
    }

    // unknown GET to the app API → empty-ish success rather than a hard failure
    if (/192\.168\.|localhost:8000|\/fantasy\//i.test(req.url)) {
      return of(new HttpResponse({ status: 200, body: { res: [], preview: true } })).pipe(
        delay(120),
      );
    }

    return next.handle(req);
  }
}
